import { Button } from "@/components/ui/button";
import { Brain, Loader2, AlertCircle } from "lucide-react";
import { useComprehensiveNutrition } from "@/contexts/NutritionContext";
import { useProfile } from "@/hooks/useProfile";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const RecalculateGoalsButton = () => {
  const { recalculateGoalsIfNeeded, isCalculating, nutritionGoals } = useComprehensiveNutrition();
  const { refetch: refetchProfile } = useProfile();
  const { toast } = useToast();
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [showManualOverrideDialog, setShowManualOverrideDialog] = useState(false);

  const handleRecalculate = async () => {
    if (isCalculating || isRecalculating) {
      return; // Prevent multiple simultaneous calls
    }

    // Check if user has manual overrides - show blocking message instead of recalculating
    if (nutritionGoals?.is_manual_override) {
      setShowManualOverrideDialog(true);
      return;
    }

    await performRecalculation();
  };

  const performRecalculation = async () => {
    try {
      setIsRecalculating(true);
      // Refetch profile first to get the latest activity level and other data
      await refetchProfile();
      await recalculateGoalsIfNeeded(true); // Force recalculation
      toast({
        title: "Goals Updated",
        description: "Your nutrition goals have been recalculated using AI based on your current profile data.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to recalculate goals. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  const isDisabled = isCalculating || isRecalculating;

  return (
    <>
      <Button 
        onClick={handleRecalculate} 
        disabled={isDisabled}
        size="sm" 
        variant="outline"
        className="gap-2"
      >
        {isDisabled ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Recalculating...
          </>
        ) : (
          <>
            <Brain className="h-4 w-4" />
            Recalculate Nutrition
          </>
        )}
      </Button>

      <AlertDialog open={showManualOverrideDialog} onOpenChange={setShowManualOverrideDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Manual Goals Are Set
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                You have manually set your calorie and/or protein goals. Recalculating would overwrite your custom values.
              </p>
              <p className="text-muted-foreground">
                To use AI-calculated goals again, edit your goals in the Nutrition dashboard - your next save will use calculated values.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Got it</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};